(() => {
  if (window.FGDesignCatalog) return;
  window.FGDesignCatalog = true;
  const instances = new WeakMap();
  const within = (root) => [
    ...(root.matches?.('[data-fg-design-catalog]') ? [root] : []),
    ...root.querySelectorAll('[data-fg-design-catalog]'),
  ];
  const init = (root) => within(root).forEach((catalog) => {
    if (instances.has(catalog)) return;
    const controller = new AbortController();
    const options = { signal: controller.signal };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanups = [];
    const refreshCarousels = [];
    catalog.querySelectorAll('[data-fg-catalog-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-fg-catalog-track]');
      const allCards = [...track.querySelectorAll('[data-fg-catalog-product]')];
      const cards = () => allCards.filter(card => !card.hidden);
      const controls = carousel.querySelector('[data-fg-catalog-controls]');
      const previous = carousel.querySelector('[data-fg-catalog-step="-1"]');
      const next = carousel.querySelector('[data-fg-catalog-step="1"]');
      const status = carousel.querySelector('[data-fg-catalog-position]');
      let frame;
      const update = () => {
        frame = undefined;
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const left = Math.max(0, track.scrollLeft);
        controls.hidden = max < 2;
        previous.disabled = left < 2;
        next.disabled = left >= max - 2;
        const activeCards = cards();
        if (!status || !activeCards.length) return;
        const bounds = track.getBoundingClientRect();
        const visible = activeCards.map((card, index) => ({ index, bounds: card.getBoundingClientRect() }))
          .filter(({ bounds: card }) => Math.min(bounds.right, card.right) - Math.max(bounds.left, card.left) > card.width * .5);
        if (!visible.length) return;
        const first = visible[0].index + 1;
        const last = visible[visible.length - 1].index + 1;
        const label = status.dataset.label.replace('__FIRST__', first).replace('__LAST__', last).replace('__TOTAL__', activeCards.length);
        if (status.textContent !== label) status.textContent = label;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const scrollTo = (left) => track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      const step = (direction) => {
        const activeCards = cards();
        if (!activeCards.length) return;
        const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
        const stride = activeCards[0].getBoundingClientRect().width + gap;
        const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / stride));
        const current = Math.round(track.scrollLeft / stride);
        scrollTo(Math.max(0, Math.min(track.scrollWidth - track.clientWidth, (current + direction * perPage) * stride)));
      };
      controls.addEventListener('click', (event) => {
        const button = event.target.closest('[data-fg-catalog-step]');
        if (!button || button.disabled) return;
        step(Number(button.dataset.fgCatalogStep));
      }, options);
      track.addEventListener('scroll', schedule, { ...options, passive: true });
      track.addEventListener('keydown', (event) => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); step(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault(); scrollTo(event.key === 'Home' ? 0 : track.scrollWidth);
        }
      }, options);
      const resize = new ResizeObserver(schedule);
      resize.observe(track);
      refreshCarousels.push(() => {
        track.scrollTo({ left: 0, behavior: 'auto' });
        if (status) status.textContent = '';
        schedule();
      });
      cleanups.push(() => { resize.disconnect(); if (frame) cancelAnimationFrame(frame); });
      update();
    });
    const filters = catalog.querySelector('[data-fg-catalog-filters]');
    if (filters) {
      const groups = [...filters.querySelectorAll('[data-fg-filter-group]')];
      const clearButton = filters.querySelector('[data-fg-filter-clear]');
      const status = filters.querySelector('[data-fg-filter-status]');
      const empty = filters.querySelector('[data-fg-filter-empty]');
      const rows = [...catalog.querySelectorAll('[data-fg-catalog-row]')];
      const jumpLinks = [...catalog.querySelectorAll('.fg-design-catalog__nav a')];
      const storageKey = `fg-collection-filters:${window.location.pathname}`;
      const state = { garment: 'all', size: 'all' };
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
        if (saved && typeof saved === 'object') {
          state.garment = saved.garment || 'all';
          state.size = saved.size || 'all';
        }
      } catch (_error) { /* Filters work when storage is unavailable. */ }
      const apply = () => {
        groups.forEach(group => {
          const kind = group.dataset.fgFilterGroup;
          const buttons = [...group.querySelectorAll('[data-fg-filter-value]')];
          if (!buttons.some(button => button.dataset.fgFilterValue === state[kind])) state[kind] = 'all';
          buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.fgFilterValue === state[kind])));
        });
        let visibleCount = 0;
        rows.forEach(row => {
          let rowCount = 0;
          row.querySelectorAll('[data-fg-catalog-product]').forEach(card => {
            const garmentMatch = state.garment === 'all' || card.dataset.fgGarment === state.garment;
            const sizeMatch = state.size === 'all' || (card.dataset.fgSizes || '|').includes(`|${state.size}|`);
            card.hidden = !(garmentMatch && sizeMatch);
            if (!card.hidden) rowCount += 1;
          });
          row.hidden = rowCount === 0;
          visibleCount += rowCount;
          const count = row.querySelector('[data-fg-catalog-count]');
          if (count) count.textContent = (rowCount === 1 ? count.dataset.labelOne : count.dataset.labelOther).replace('__COUNT__', rowCount);
          jumpLinks.filter(link => link.getAttribute('href') === `#${row.id}`).forEach(link => { link.hidden = row.hidden; });
        });
        const navigation = catalog.querySelector('.fg-design-catalog__nav');
        if (navigation) navigation.hidden = visibleCount === 0;
        clearButton.hidden = state.garment === 'all' && state.size === 'all';
        empty.hidden = visibleCount !== 0;
        status.textContent = filters.dataset.fgCountTemplate.replace('__COUNT__', visibleCount);
        refreshCarousels.forEach(refresh => refresh());
        try { window.sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (_error) { /* Storage is optional. */ }
      };
      groups.forEach(group => group.addEventListener('click', event => {
        const button = event.target.closest('[data-fg-filter-value]');
        if (!button) return;
        state[group.dataset.fgFilterGroup] = button.dataset.fgFilterValue;
        apply();
      }, options));
      clearButton.addEventListener('click', () => {
        state.garment = 'all'; state.size = 'all'; apply();
      }, options);
      filters.hidden = false;
      apply();
    }
    catalog.addEventListener('shopify:block:select', (event) => {
      const blockId = event.detail?.blockId;
      const row = blockId && catalog.querySelector(`[id$="-${CSS.escape(blockId)}"]`);
      (row || event.target.closest('[data-fg-catalog-row]'))?.scrollIntoView({ block: 'center', behavior: 'instant' });
    }, options);
    instances.set(catalog, () => { controller.abort(); cleanups.forEach(cleanup => cleanup()); instances.delete(catalog); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => within(event.target).forEach(catalog => instances.get(catalog)?.()));
})();
